import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import toast from "react-hot-toast";
import { t } from "i18next";
import { useFormik } from "formik";
import styles from "./Account.module.css";
import Button from "../../Buttons/Button/Button";
import FormField from "../../UpdatedFormUI/FormField/FormField";
import { useNavigate } from "react-router-dom";
import { updateUser } from "../../../../services/userService";
import { fetchUserByTokenWithoutLoading } from "../../../../app/features/userSlice";
import { useDispatch, useSelector } from "react-redux";
import Subtitle from "../Subtitle/Subtitle";
import { FcGoogle } from "react-icons/fc"; // Google icon
import { FaFacebook } from "react-icons/fa"; // Facebook icon
import axios from "axios";
import {
    socialLogin,
    disconnectSocialAccount,
    connectSocialAccount,
} from "../../../../services/authService";
import { useGoogleLogin } from "@react-oauth/google"; // Google login hook
import FacebookLogin from "react-facebook-login/dist/facebook-login-render-props"; // Facebook login with custom render

function Account() {
    const [formLoading, setFormLoading] = useState(false);
    const [editMode, setEditMode] = useState(true);
    const [googleConnected, setGoogleConnected] = useState(false); // State for Google account connection
    const [facebookConnected, setFacebookConnected] = useState(false); // State for Facebook account connection
    const [googleLoading, setGoogleLoading] = useState(false); // Loading state for Google connection
    const [facebookLoading, setFacebookLoading] = useState(false); // Loading state for Facebook connection

    const user = useSelector((state) => state.user.user);
    const userToken = useSelector((state) => state.user.token);
    const dispatch = useDispatch();

    // Update social account connection status when user data changes
    useEffect(() => {
        if (user) {
            setGoogleConnected(!!user.googleLogin);
            setFacebookConnected(!!user.facebookLogin);
        }
    }, [user]);

    // Formik configuration for password change form
    const formik = useFormik({
        initialValues: {
            password: "",
            password_repeat: "",
        },
        validationSchema: Yup.object({
            password: Yup.string().min(
                8,
                t("forms.create_account.must_be_at_least_8_characters")
            ),
            password_repeat: Yup.string().when("password", {
                is: (val) => val && val?.length > 0,
                then: (schema) =>
                    schema
                        .oneOf(
                            [Yup.ref("password"), null],
                            t("forms.create_account.passwords_must_match")
                        )
                        .required(t("forms.create_account.required")),
                otherwise: (schema) => schema.notRequired(),
            }),
        }),
        onSubmit: (values) => {
            if (formLoading) return;

            setFormLoading(true);
            const formData = new FormData();
            formData.append("token", userToken);

            if (values.password) {
                formData.append("password", values.password);
            }

            toast
                .promise(updateUser(formData), {
                    loading: t("edit_profile.updating_profile"),
                    success: <b>{t("edit_profile.profile_updated")}</b>,
                    error: (err) => <b>{err.response.data.error}</b>,
                })
                .then(async () => {
                    await dispatch(fetchUserByTokenWithoutLoading(userToken));
                    const target = document.querySelector(".formScroll");
                    if (target) {
                        window.scrollTo({
                            top: target.offsetTop,
                            behavior: "smooth",
                        });
                    }
                    await setEditMode(!editMode);
                    await setFormLoading(false);
                })
                .catch((error) => {
                    setFormLoading(false);
                    if (error?.response?.data?.field) {
                        formik.setErrors({
                            [error.response.data.field]: error.response.data.error,
                        });
                    }
                });
        },
    });

    // Google Login Handler
    const googleLogin = useGoogleLogin({
        onSuccess: async (response) => {
            setGoogleLoading(true); // Disable the Google Connect button
            try {
                const googleUserResponse = await axios.get(
                    `https://www.googleapis.com/oauth2/v1/userinfo?access_token=${response.access_token}`
                );
                const googleUserData = googleUserResponse.data;

                const result = await connectSocialAccount(
                    "google",
                    { ...googleUserData, access_token: response.access_token },
                    user.id
                );

                if (result.data.success) {
                    toast.success("Google account connected successfully");
                    await dispatch(fetchUserByTokenWithoutLoading(userToken)); // Refresh user data
                }
            } catch (error) {
                if (error.error === "This social account is already linked to another user.") {
                    toast.error(t("general.UI.Thissocialaccountisalreadylinkedtoanotheruser"));
                } else {
                    toast.error(error.error || t("general.UI.FailedtoconnectGoogleaccount"));
                }
            } finally {
                setGoogleLoading(false); // Re-enable the Google Connect button
            }
        },
        onError: (error) => {
            toast.error("Google login failed");
            setGoogleLoading(false); // Re-enable the Google Connect button
        },
    });

    // Facebook Login Handler
    const responseFacebook = async (response) => {
        setFacebookLoading(true); // Disable the Facebook Connect button
        try {
            if (response.accessToken) {
                const facebookUserData = await axios.get(
                    `https://graph.facebook.com/me?fields=id,name,email&access_token=${response.accessToken}`
                );

                const result = await connectSocialAccount(
                    "facebook",
                    { ...facebookUserData.data, access_token: response.accessToken },
                    user.id
                );

                if (result.data.success) {
                    toast.success("Facebook account connected successfully");
                    await dispatch(fetchUserByTokenWithoutLoading(userToken)); // Refresh user data
                }
            }
        } catch (error) {
            if (error.error === "This social account is already linked to another user.") {
                toast.error(t("general.UI.Thissocialaccountisalreadylinkedtoanotheruser"));
            } else {
                toast.error(error.error || t("general.UI.FailedtoconnectFacebookaccount"));
            }
        } finally {
            setFacebookLoading(false); // Re-enable the Facebook Connect button
        }
    };

    // Handle Google Disconnect
    const handleGoogleDisconnect = async () => {
        try {
            setGoogleLoading(true); // Disable the Google Connect button
            await disconnectSocialAccount("google", user.id);
            toast.success("Google account disconnected successfully");
            await dispatch(fetchUserByTokenWithoutLoading(userToken));
            setGoogleLoading(false); // Disable the Google Connect button
        } catch (error) {
            toast.error("Failed to disconnect Google account");
            setGoogleLoading(false); // Disable the Google Connect button
        }
    };

    // Handle Facebook Disconnect
    const handleFacebookDisconnect = async () => {
        try {
            setFacebookLoading(true); // Disable the Google Connect button
            await disconnectSocialAccount("facebook", user.id);
            toast.success("Facebook account disconnected successfully");
            await dispatch(fetchUserByTokenWithoutLoading(userToken)); // Refresh user data
            setFacebookLoading(false); // Disable the Google Connect button
        } catch (error) {
            toast.error("Failed to disconnect Facebook account");
            setFacebookLoading(false); // Disable the Google Connect button
        }
    };

    return (
        <div className={`${styles.container} formScroll`}>
            {/* Social Accounts Section */}
            <div className={styles.socialAccountsContainer}>
                <Subtitle dark>{t("general.UI.social_accounts")}</Subtitle>

                {/* Google Account Connection */}
                <div className={styles.socialAccount}>
                    <div className={styles.socialAccountLabel}>
                        <FcGoogle size={24} /> {/* Google icon */}
                        <span>{t("general.UI.Google")}</span>
                    </div>
                    {googleConnected ? (
                        <Button
                            onClick={googleLoading ? () => { } : handleGoogleDisconnect}
                            className={styles.disconnectButton}
                        >
                            {t("general.UI.disconnect")}
                        </Button>
                    ) : (
                        <Button
                            onClick={googleLogin}
                            className={styles.connectButton}
                            disabled={googleLoading} // Disable button when loading
                        >
                            {googleLoading ? t("general.UI.loading") : t("general.UI.connect")}
                        </Button>
                    )}
                </div>

                {/* Facebook Account Connection */}
                <div className={styles.socialAccount}>
                    <div className={styles.socialAccountLabel}>
                        <FaFacebook size={24} color="#1877F2" /> {/* Facebook icon */}
                        <span>{t("general.UI.Facebook")}</span>
                    </div>
                    {facebookConnected ? (
                        <Button
                            onClick={facebookLoading ? () => { } : handleFacebookDisconnect}
                            className={styles.disconnectButton}
                            disabled={googleLoading} // Disable button when loading
                        >
                            {t("general.UI.disconnect")}
                        </Button>
                    ) : (
                        <FacebookLogin
                            appId={process.env.REACT_APP_FB_APP_ID} // Your Facebook App ID
                            fields="name,email,picture"
                            callback={responseFacebook}
                            render={(renderProps) => (
                                <Button
                                    onClick={renderProps.onClick}
                                    className={styles.connectButton}
                                    disabled={facebookLoading} // Disable button when loading
                                >
                                    {facebookLoading ? t("general.UI.loading") : t("general.UI.connect")}
                                </Button>
                            )}
                        />
                    )}
                </div>
            </div>

            {/* Password Change Section */}
            <div className={styles.editContainer}>
                <Subtitle dark>{t("edit_profile.job_seeker.change_password_title")}</Subtitle>

                <FormField
                    name="password"
                    type="password"
                    label={t("edit_profile.job_seeker.password")}
                    hasBorder
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password}
                    error={formik.touched.password && formik.errors.password}
                />

                <FormField
                    name="password_repeat"
                    type="password"
                    label={t("edit_profile.job_seeker.password_repeat")}
                    hasBorder
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    value={formik.values.password_repeat}
                    error={formik.touched.password_repeat && formik.errors.password_repeat}
                />

                <Button onClick={formik.submitForm} className={styles.saveButton}>
                    {formLoading ? t("general.UI.loading") : t("general.UI.save")}
                </Button>
            </div>
        </div>
    );
}

export default Account;